import React, { useState } from "react";
import {
  registrarUsuarioSocial,
  loginUsuarioSocial,
  registrarUsuario,
  signInWithEmailAndPassword,
} from "../api";
import { useUser } from "../context/AuthContext";
import logo from "../assets/lazerzonelogo.png";
import ParticleBackground from "../components/ParticleBackground";

import { auth, googleProvider } from "../services/firebase";
import { signInWithPopup } from "firebase/auth";
import { useNavigate } from "react-router-dom";

const brand = {
  nearBlack: '#141318',
  darkGray: '#1F2937',
  light: '#E9EAE8',
  white: '#FFFFFF',
  blueGray: '#566D87',
  gold: '#FFD404',
  ink: '#00181C',
};

const Auth = () => {
  const { verificarAutenticacion } = useUser();

  const [activeTab, setActiveTab] = useState("login");
  const [mensaje, setMensaje] = useState("");
  const [mensajeTipo, setMensajeTipo] = useState(""); // 'success', 'error', 'loading', 'info'
  const [isLoading, setIsLoading] = useState(false);
  const Navigate = useNavigate();
  
  const mostrarMensaje = (texto, tipo = "info") => {
    setMensaje(texto);
    setMensajeTipo(tipo);
    if (tipo !== "loading") {
      setTimeout(() => {
        setMensaje("");
        setMensajeTipo("");
      }, 5000);
    }
  };

  const limpiarMensaje = () => {
    setMensaje("");
    setMensajeTipo("");
  };
  const handleSocialRegister = (provider) => {
    setIsLoading(true);
    mostrarMensaje("Iniciando registro con Google...", "loading");
    
    signInWithPopup(auth, provider)
      .then((result) => {
        const user = result.user;
        const nombre = user.displayName || "Nombre no disponible";
        const correo = user.email || "Correo no disponible";
        const providerId = user.providerData[0]?.providerId || "Proveedor no disponible";
        const uid = user.uid || "UID no disponible";
        
        mostrarMensaje("Registrando usuario en el sistema...", "loading");
        
        registrarUsuarioSocial({
          nombre,
          correo,
          provider: providerId,
          uid,
        })
          .then((data) => {
            setIsLoading(false);
            limpiarMensaje();
            mostrarMensaje(`¡Bienvenido ${nombre}! Registro exitoso`, "success");
            setTimeout(() => Navigate("/"), 2000);
          })
          .catch((err) => {
            setIsLoading(false);
            // Solo log en desarrollo
            if (process.env.NODE_ENV === 'development') {
              console.error("Error al registrar usuario social:", err);
            }
            const errorMsg = err.response?.data?.message || err.message || "Error al registrarse con Google";
            mostrarMensaje(errorMsg, "error");
          });
      })
      .catch((error) => {
        setIsLoading(false);
        // Solo log en desarrollo
        if (process.env.NODE_ENV === 'development') {
          console.error("Error en registro social: ", error.message);
        }
        mostrarMensaje("Error al conectar con Google", "error");
      });
  };

  const handleSocialLogin = (provider) => {
    setIsLoading(true);
    mostrarMensaje("Iniciando sesión con Google...", "loading");
    
    signInWithPopup(auth, provider)
      .then(async (result) => {
        const user = result.user;
        const token = await user.getIdToken();
        const nombre = user.displayName || "Nombre no disponible";
        const correo = user.email || "Correo no disponible";
        const uid = user.uid;

        mostrarMensaje("Verificando credenciales...", "loading");

        try {
          const response = await loginUsuarioSocial({
            nombre,
            correo,
            provider: provider.providerId,
            uid,
            token,
          });

          if (
            response.message === "Usuario autenticado correctamente" ||
            response.message === "Usuario registrado con éxito"
          ) {
            mostrarMensaje("Actualizando sesión...", "loading");
            await verificarAutenticacion();
            setIsLoading(false);
            limpiarMensaje();
            mostrarMensaje(`¡Bienvenido ${nombre}!`, "success");
            setTimeout(() => Navigate("/"), 2000);
          }
        } catch (error) {
          setIsLoading(false);
          // Solo log en desarrollo
          if (process.env.NODE_ENV === 'development') {
            console.error("Error en login social: ", error.message);
          }
          const errorMsg = error.response?.data?.message || error.message || "Error al iniciar sesión";
          mostrarMensaje(errorMsg, "error");
        }
      })
      .catch((error) => {
        setIsLoading(false);
        // Solo log en desarrollo
        if (process.env.NODE_ENV === 'development') {
          console.error("Error en login social: ", error.message);
        }
        mostrarMensaje("Error al conectar con Google", "error");
      });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const email = document.getElementById("loginEmail").value;
    const password = document.getElementById("loginPassword").value;

    if (!email || !password) {
      mostrarMensaje("Por favor, completa todos los campos", "error");
      return;
    }

    setIsLoading(true);
    mostrarMensaje("Iniciando inicio de sesión...", "loading");

    try {
      mostrarMensaje("Verificando credenciales...", "loading");
      await signInWithEmailAndPassword({ email, password });
      
      mostrarMensaje("Actualizando sesión...", "loading");
      await verificarAutenticacion();
      
      setIsLoading(false);
      limpiarMensaje();
      mostrarMensaje("¡Inicio de sesión exitoso!", "success");
      setTimeout(() => Navigate("/"), 2000);
    } catch (error) {
      setIsLoading(false);
      // Solo log en desarrollo
      if (process.env.NODE_ENV === 'development') {
        console.error("Error en login: ", error);
      }
      
      // Manejo específico de errores
      let errorMessage = "Error al iniciar sesión";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      mostrarMensaje(errorMessage, "error");
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const name = document.getElementById("registerName").value;
    const username = document.getElementById("registerUsername").value;
    const email = document.getElementById("registerEmail").value;
    const password = document.getElementById("registerPassword").value;
    const repeatPassword = document.getElementById("repeatPassword").value;

    // Validaciones
    if (!name || !username || !email || !password || !repeatPassword) {
      mostrarMensaje("Por favor, completa todos los campos", "error");
      return;
    }

    if (password !== repeatPassword) {
      mostrarMensaje("Las contraseñas no coinciden", "error");
      return;
    }

    if (password.length < 6) {
      mostrarMensaje("La contraseña debe tener al menos 6 caracteres", "error");
      return;
    }

    setIsLoading(true);
    mostrarMensaje("Iniciando registro...", "loading");

    try {
      mostrarMensaje("Creando cuenta...", "loading");
      await registrarUsuario({ name, username, email, password });
      
      setIsLoading(false);
      limpiarMensaje();
      mostrarMensaje("¡Registro exitoso! Ahora puedes iniciar sesión", "success");
      
      // Cambiar automáticamente al tab de login después del registro
      setTimeout(() => {
        setActiveTab("login");
        document.getElementById("loginEmail").value = email;
      }, 2000);
      
    } catch (error) {
      setIsLoading(false);
      // Solo log en desarrollo
      if (process.env.NODE_ENV === 'development') {
        console.error("Error en registro: ", error);
      }
      
      let errorMessage = "Error al registrar usuario";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      mostrarMensaje(errorMessage, "error");
    }
  };

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center py-8 sm:py-12 md:py-16 overflow-x-hidden" style={{ background: `radial-gradient(1000px 500px at 10% -10%, rgba(255, 212, 4, 0.06), transparent 70%), radial-gradient(900px 500px at 110% 10%, rgba(86, 109, 135, 0.06), transparent 70%), ${brand.nearBlack}`, color: brand.light }}>
      {/* Fondo decorativo con "luces" + partículas */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <ParticleBackground className="opacity-25" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full blur-3xl opacity-70 animate-pulse" style={{ background: 'radial-gradient(circle at 30% 30%, rgba(255,212,4,0.18), transparent 60%)' }} />
        <div className="absolute bottom-0 right-0 w-[320px] h-[220px] rounded-full blur-2xl opacity-60 animate-pulse" style={{ background: 'radial-gradient(circle at 70% 70%, rgba(86,109,135,0.18), transparent 60%)' }} />
      </div>

      <div className="relative z-10 w-full max-w-none px-2 sm:px-4 md:px-8" style={{ width: '70%', maxWidth: '600px' }}>
        <div className="rounded-3xl shadow-2xl px-6 sm:px-8 md:px-10 py-8 sm:py-10 md:py-12 flex flex-col items-center backdrop-blur-xl animate-fade-in" style={{ backgroundColor: 'rgba(31,41,55,0.55)', border: `1px solid ${brand.blueGray}33` }}>
          {/* Logo */}
          <div className="mb-8 flex items-center justify-center">
            <img
              src={logo}
              alt="Laser Zone Logo"
              className="w-32 h-auto sm:w-40"
            />
          </div>

          {/* Mensaje de estado mejorado */}
          {mensaje && (
            <div 
              className={`w-full mb-6 p-4 rounded-lg text-center font-medium flex items-center justify-center gap-3 animate-fade-in ${
                mensajeTipo === 'loading' ? 'animate-pulse' : ''
              }`} 
              style={{ 
                backgroundColor: 
                  mensajeTipo === 'success' ? 'rgba(34, 197, 94, 0.1)' :
                  mensajeTipo === 'error' ? 'rgba(239, 68, 68, 0.1)' :
                  mensajeTipo === 'loading' ? 'rgba(59, 130, 246, 0.1)' :
                  'rgba(255, 212, 4, 0.1)',
                color: 
                  mensajeTipo === 'success' ? '#22c55e' :
                  mensajeTipo === 'error' ? '#ef4444' :
                  mensajeTipo === 'loading' ? '#3b82f6' :
                  brand.gold,
                border: `1px solid ${
                  mensajeTipo === 'success' ? '#22c55e55' :
                  mensajeTipo === 'error' ? '#ef444455' :
                  mensajeTipo === 'loading' ? '#3b82f655' :
                  brand.gold + '55'
                }`
              }}
            >
              {mensajeTipo === 'loading' && (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
              )}
              {mensajeTipo === 'success' && (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
              {mensajeTipo === 'error' && (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              )}
              <span>{mensaje}</span>
            </div>
          )}

          {/* Tabs de navegación */}
          <div className="w-full flex mb-6 rounded-xl overflow-hidden" style={{ backgroundColor: 'rgba(20,19,24,0.8)', border: `1px solid ${brand.blueGray}33` }}>
            <button
              className={`flex-1 py-3 px-4 font-bold text-base transition-all duration-200 ${activeTab === "login" ? 'text-white' : 'text-gray-400'}`}
              style={activeTab === "login" ? { backgroundColor: brand.gold, color: brand.ink } : {}}
              onClick={() => setActiveTab("login")}
            >
              Sign In
            </button>
            <button
              className={`flex-1 py-3 px-4 font-bold text-base transition-all duration-200 ${activeTab === "register" ? 'text-white' : 'text-gray-400'}`}
              style={activeTab === "register" ? { backgroundColor: brand.gold, color: brand.ink } : {}}
              onClick={() => setActiveTab("register")}
            >
              Sign Up
            </button>
          </div>

          {/* Contenido del tab activo */}
          <div className="w-full">
            {activeTab === "login" && (
              <form onSubmit={handleLogin} className="flex flex-col gap-4">
                {/* Login con Google */}
                <div className="text-center mb-4">
                  <p className="text-sm font-medium mb-3" style={{ color: brand.blueGray }}>
                    Sign in with:
                  </p>
                  <button
                    type="button"
                    className="w-full py-3 rounded-lg font-semibold text-base transition-all duration-200 hover:opacity-90 flex items-center justify-center gap-2"
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', color: brand.white, border: `1px solid ${brand.blueGray}55` }}
                    onClick={() => handleSocialLogin(googleProvider)}
                  >
                    <i className="fab fa-google text-lg"></i>
                    Continue with Google
                  </button>
                </div>

                <div className="flex items-center gap-4 my-4">
                  <div className="flex-1 h-px" style={{ backgroundColor: brand.blueGray }}></div>
                  <span className="text-sm font-medium" style={{ color: brand.blueGray }}>or with email</span>
                  <div className="flex-1 h-px" style={{ backgroundColor: brand.blueGray }}></div>
                </div>

                {/* Campos del formulario */}
                <div className="flex flex-col gap-4">
                  <input
                    type="email"
                    id="loginEmail"
                    className="w-full px-4 py-3 rounded-lg text-white text-base focus:outline-none focus:ring-2 transition-all"
                    style={{ backgroundColor: brand.darkGray, border: `1px solid ${brand.blueGray}55`, focusRingColor: brand.gold }}
                    placeholder="Email address"
                  />
                  <input
                    type="password"
                    id="loginPassword"
                    className="w-full px-4 py-3 rounded-lg text-white text-base focus:outline-none focus:ring-2 transition-all"
                    style={{ backgroundColor: brand.darkGray, border: `1px solid ${brand.blueGray}55`, focusRingColor: brand.gold }}
                    placeholder="Password"
                  />
                </div>

                <div className="flex items-center justify-between mt-2 mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      id="rememberMe"
                      className="w-4 h-4 rounded"
                      style={{ accentColor: brand.gold }}
                    />
                    <span className="text-sm" style={{ color: brand.blueGray }}>Remember me</span>
                  </label>
                  <a href="#!" className="text-sm hover:underline transition-all" style={{ color: brand.gold }}>
                    Forgot password?
                  </a>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full py-3 rounded-lg font-bold text-base transition-all duration-200 shadow-lg flex items-center justify-center gap-2 ${
                    isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:opacity-90'
                  }`}
                  style={{ backgroundColor: brand.gold, color: brand.ink }}
                >
                  {isLoading && (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                  )}
                  {isLoading ? 'Iniciando sesión...' : 'Sign In'}
                </button>
              </form>
            )}

            {activeTab === "register" && (
              <form onSubmit={handleRegister} className="flex flex-col gap-4">
                {/* Registro con Google */}
                <div className="text-center mb-4">
                  <p className="text-sm font-medium mb-3" style={{ color: brand.blueGray }}>
                    Sign up with:
                  </p>
                  <button
                    type="button"
                    className="w-full py-3 rounded-lg font-semibold text-base transition-all duration-200 hover:opacity-90 flex items-center justify-center gap-2"
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', color: brand.white, border: `1px solid ${brand.blueGray}55` }}
                    onClick={() => handleSocialRegister(googleProvider)}
                  >
                    <i className="fab fa-google text-lg"></i>
                    Continue with Google
                  </button>
                </div>

                <div className="flex items-center gap-4 my-4">
                  <div className="flex-1 h-px" style={{ backgroundColor: brand.blueGray }}></div>
                  <span className="text-sm font-medium" style={{ color: brand.blueGray }}>or with your information</span>
                  <div className="flex-1 h-px" style={{ backgroundColor: brand.blueGray }}></div>
                </div>

                {/* Campos de registro */}
                <div className="flex flex-col gap-4">
                  <input
                    type="text"
                    id="registerName"
                    className="w-full px-4 py-3 rounded-lg text-white text-base focus:outline-none focus:ring-2 transition-all"
                    style={{ backgroundColor: brand.darkGray, border: `1px solid ${brand.blueGray}55`, focusRingColor: brand.gold }}
                    placeholder="Full name"
                  />
                  <input
                    type="text"
                    id="registerUsername"
                    className="w-full px-4 py-3 rounded-lg text-white text-base focus:outline-none focus:ring-2 transition-all"
                    style={{ backgroundColor: brand.darkGray, border: `1px solid ${brand.blueGray}55`, focusRingColor: brand.gold }}
                    placeholder="Username"
                  />
                  <input
                    type="email"
                    id="registerEmail"
                    className="w-full px-4 py-3 rounded-lg text-white text-base focus:outline-none focus:ring-2 transition-all"
                    style={{ backgroundColor: brand.darkGray, border: `1px solid ${brand.blueGray}55`, focusRingColor: brand.gold }}
                    placeholder="Email address"
                  />
                  <input
                    type="password"
                    id="registerPassword"
                    className="w-full px-4 py-3 rounded-lg text-white text-base focus:outline-none focus:ring-2 transition-all"
                    style={{ backgroundColor: brand.darkGray, border: `1px solid ${brand.blueGray}55`, focusRingColor: brand.gold }}
                    placeholder="Password"
                  />
                  <input
                    type="password"
                    id="repeatPassword"
                    className="w-full px-4 py-3 rounded-lg text-white text-base focus:outline-none focus:ring-2 transition-all"
                    style={{ backgroundColor: brand.darkGray, border: `1px solid ${brand.blueGray}55`, focusRingColor: brand.gold }}
                    placeholder="Confirm password"
                  />
                </div>

                <label className="flex items-start gap-3 mt-2 mb-4 cursor-pointer">
                  <input
                    type="checkbox"
                    id="termsCheck"
                    className="w-4 h-4 rounded mt-1"
                    style={{ accentColor: brand.gold }}
                  />
                  <span className="text-sm leading-relaxed" style={{ color: brand.blueGray }}>
                    I accept the <a href="#!" className="hover:underline" style={{ color: brand.gold }}>terms and conditions</a>
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full py-3 rounded-lg font-bold text-base transition-all duration-200 shadow-lg flex items-center justify-center gap-2 ${
                    isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:opacity-90'
                  }`}
                  style={{ backgroundColor: brand.gold, color: brand.ink }}
                >
                  {isLoading && (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                  )}
                  {isLoading ? 'Registrando...' : 'Sign Up'}
                </button>
              </form>
            )}
          </div>

          {/* Links legales discretos */}
          <div className="mt-8 pt-6 border-t border-opacity-30 text-center" style={{ borderColor: brand.blueGray }}>
            <div className="flex items-center justify-center gap-4 text-xs">
              <a 
                href="#!" 
                className="hover:underline transition-all duration-200" 
                style={{ color: brand.blueGray }}
              >
                Privacy Policy
              </a>
              <span style={{ color: brand.blueGray }}>•</span>
              <a 
                href="#!" 
                className="hover:underline transition-all duration-200" 
                style={{ color: brand.blueGray }}
              >
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
